import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";

type URLParams = Record<string, any>

//initialValues: {arg1:2}

//params
//[url]
//{arg1:2}

//[url]?arg2=2
//{arg1=2, arg2=2}

//[url]?arg1=2&arg2=2
//{arg1=2, arg2=2}

//set Params
//{}=>[url]
//{arg1:2}=>[url]/arg1=2
//{arg1:3, arg2:2}=>[url]/arg1=3&arg2=2

export default function useURLParams<T extends URLParams>(type:"search"|"hash", defaultValues:T){
    const location = useLocation();
    const navigate = useNavigate();
    
    const urlToObject = useCallback((urlString:string)=>{
        const retParams = {...defaultValues} as URLParams;
        if(!urlString) return retParams;

        //Remove first symbol
        urlString = urlString.substring(1);

        for(const searchParam of new URLSearchParams(urlString)){
            let [name, stringValue] = searchParam;
            name = decodeURIComponent(name);
            stringValue = decodeURIComponent(stringValue);

            if(stringValue == "") retParams[name] = "";
            else retParams[name] = JSON.parse(stringValue);
        }

        return retParams;
    },[defaultValues])

    const objectToURL = useCallback((urlObject:URLParams)=>{
        const paramStringList:string[] = [];
        
        for(let [name, value] of Object.entries(urlObject)){
            if(value !== undefined){
                if(value === "") paramStringList.push(`${encodeURIComponent(name)}`);
                else paramStringList.push(`${encodeURIComponent(name)}=${encodeURIComponent(JSON.stringify(value))}`);
            }
        }

        let paramsString = paramStringList.join("&");
        paramsString = type==="search"?"?" + paramsString:"#" + paramsString;

        return paramsString;
    }, [type]);

    const params = useMemo(()=>urlToObject(location[type]), [location, type]);

    const setParams:Dispatch<SetStateAction<T>> = useCallback((newParams:SetStateAction<T>)=>{
        const prevParams = urlToObject(location[type]) as T;

        let finalParams:URLParams;
        if(typeof newParams === "function") finalParams = newParams(prevParams);
        else finalParams = newParams;

        const paramsString = objectToURL(finalParams);
        const url = location.pathname + (type==="search"?paramsString:location.search) + (type==="hash"?paramsString:location.hash);
        
        navigate(url);
    }, [location, type]);

    return [params, setParams] as [T, Dispatch<SetStateAction<T>>];
}